import React from "react";
import { Link, graphql } from "gatsby";
import Image from "gatsby-image";
import { DiscussionEmbed } from "disqus-react";
import Layout from "../components/layout";
import SEO from "../components/seo";
import styles from "./blog-post.module.scss";
import { CardBox } from "../components/cardbox";
import { Progress } from "../components/progress";

const disqusShortname = "martinschnurer-net-1";

interface BlogPostTemplate {
  location: any;
  data: any;
  pageContext: any;
}

interface RecommendedPosts {
  score: number;
  post: any;
}

const BlogPostTemplate: React.FC<BlogPostTemplate> = props => {
  const post = props.data.thisPost;
  const { previous, next } = props.pageContext;

  const allPosts = props.data.allPosts;

  const recomScores: RecommendedPosts[] = allPosts.edges.map(edge => {
    const { tags } = edge.node.frontmatter;

    const similarTags =
      post.frontmatter.tags && tags
        ? post.frontmatter.tags.reduce(
            (acc, tag) => (tags.includes(tag) ? acc + 1 : acc),
            0
          )
        : 0;

    return {
      // do not recommend the same post
      score: post.fields.slug === edge.node.fields.slug ? 0 : similarTags,
      post: edge.node
    };
  });

  recomScores.sort((a, b) => (a.score < b.score ? 1 : -1));

  const toRecommend = recomScores.slice(0, 3).filter(score => {
    if (score.post.fields.slug === post.fields.slug) {
      return false;
    }
    return true;
  });

  const postStats = post.wordCount;

  const { words, sentences, paragraphs } = postStats;

  return (
    <Layout>
      <CardBox padding={false}>
        <div className={styles.blog_post}>
          <SEO
            title={post.frontmatter.title}
            description={post.frontmatter.description || post.excerpt}
          />
          {post.frontmatter.cover && (
            <div className={styles.imageWrapper}>
              <Image
                className={styles.topImage}
                fluid={post.frontmatter.cover.childImageSharp.fluid}
                alt="post cover"
              />
              {post.frontmatter.coverCredits && (
                <div className={styles.author}>
                  {post.frontmatter.coverCredits}
                </div>
              )}
            </div>
          )}

          <div className={styles.padded}>
            {process.env.NODE_ENV === "development" && (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>Words</th>
                      <th>Sentences</th>
                      <th>Paragraphs</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{words}</td>
                      <td>{sentences}</td>
                      <td>{paragraphs}</td>
                    </tr>
                  </tbody>
                </table>
                <Progress value={(words * 100) / 2500} />
              </>
            )}
            <div className={styles.blogHtml}>
              <h1 className={styles.mainTitle + " mb-6"}>
                {post.frontmatter.title.toUpperCase()}
              </h1>
              <div className={styles.date}>{post.frontmatter.date}</div>
              <div
                className={styles.blog_post}
                dangerouslySetInnerHTML={{ __html: post.html }}
              />
            </div>

            <hr style={{ marginBottom: 10 }} />
            <div className={styles.bottomNavigation + " mt-20"}>
              <div className={styles.navigationLeft}>
                {previous && (
                  <Link to={previous.fields.slug} rel="prev">
                    ← {previous.frontmatter.title}
                  </Link>
                )}
              </div>
              <div className={styles.navigationRight}>
                {next && (
                  <Link to={next.fields.slug} rel="next">
                    {next.frontmatter.title} →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardBox>

      <CardBox style={{ marginTop: 15, marginBottom: 25, padding: "0px 20px" }}>
        <div className={styles.recommendPanel}>
          <h1 className="text-3xl font-semibold mb-3">You may also like</h1>
          <div className={styles.postList}>
            {toRecommend.map(obj => (
              <Link
                key={obj.post.fields.slug}
                to={obj.post.fields.slug}
                style={{ textDecoration: "none" }}
              >
                <div
                  className={
                    styles.post +
                    " " +
                    "hover:shadow-xl hover:border-transparent transition-all border rounded-xl transform hover:-translate-y-2 duration-300"
                  }
                >
                  {obj.post.frontmatter.cover ? (
                    <Image
                      className={styles.topImage}
                      fluid={obj.post.frontmatter.cover.childImageSharp.fluid}
                      alt="post cover"
                    />
                  ) : (
                    <div className={styles.emptyCover} />
                  )}
                  <div className={styles.padded}>
                    <h1 className="mb-6">{obj.post.frontmatter.title}</h1>
                    <p>{obj.post.excerpt}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </CardBox>
      <CardBox style={{ marginTop: 15, marginBottom: 25 }}>
        <div className={styles.discuss_box}>
          <EmbeddedDis
            postId={post.id}
            title={post.frontmatter.title}
            url=""
            // url={`/${post.frontmatter.title}`}
          />
        </div>
      </CardBox>
    </Layout>
  );
};

interface EmbeddedDisProps {
  postId: string;
  title: string;
  url: string;
}

const EmbeddedDis: React.FC<EmbeddedDisProps> = React.memo(
  ({ postId, title, url }) => (
    <DiscussionEmbed
      shortname={disqusShortname}
      config={{
        url,
        identifier: postId,
        title
      }}
    />
  )
);

export default BlogPostTemplate;

export const pageQuery = graphql`
  query BlogPostBySlug($slug: String!) {
    site {
      siteMetadata {
        title
        author
      }
    }
    thisPost: markdownRemark(fields: { slug: { eq: $slug } }) {
      id
      excerpt(pruneLength: 160)
      html
      fields {
        slug
      }
      wordCount {
        paragraphs
        sentences
        words
      }
      frontmatter {
        title
        date(formatString: "MMMM DD, YYYY")
        categories
        tags
        cover {
          childImageSharp {
            fluid(
              traceSVG: { color: "#29276c", optCurve: true, threshold: 50 }
            ) {
              ...GatsbyImageSharpFluid_tracedSVG
            }
          }
        }
        coverCredits
      }
    }
    allPosts: allMarkdownRemark {
      edges {
        node {
          fields {
            slug
          }
          excerpt
          frontmatter {
            categories
            title
            tags
            cover {
              childImageSharp {
                fluid(
                  traceSVG: { color: "#29276c", optCurve: true, threshold: 50 }
                ) {
                  ...GatsbyImageSharpFluid_tracedSVG
                }
              }
            }
          }
          timeToRead
          id
        }
      }
    }
  }
`;
